import { buildEventRoles } from './build-event-roles';
import { EventAccessItem, EventRoleFlags, EventSourceEntry } from 'src/app/types/event-access.types';

type Flags = Partial<EventRoleFlags>;

/** OR de los flags: que una fuente no traiga un permiso no apaga el que trajo otra. */
function mergeFlags(a: Flags, b: Flags): Flags {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)] as (keyof EventRoleFlags)[]);
  return [...keys].reduce<Flags>((acc, key) => ({ ...acc, [key]: !!a[key] || !!b[key] }), {});
}

function mergeEntry(current: EventSourceEntry | undefined, next: EventSourceEntry): EventSourceEntry {
  if (!current) {
    return next;
  }
  return {
    event: current.event,
    flags: mergeFlags(current.flags, next.flags),
    // `capabilities` solo las trae my-events: la primera que llegue gana.
    capabilities: current.capabilities ?? next.capabilities,
  };
}

/**
 * Mismo orden en que paginan las fuentes, para que "ver más" agregue al final:
 * vigentes por inicio ascendente (el próximo primero); finalizados e inactivos por fin
 * descendente (el más reciente primero).
 */
export type EventListOrder = 'upcoming' | 'past';

const COMPARATORS: Readonly<Record<EventListOrder, (a: EventAccessItem, b: EventAccessItem) => number>> = {
  upcoming: (a, b) => a.event.initialDate.localeCompare(b.event.initialDate) || a.event.id - b.event.id,
  past: (a, b) => b.event.finishDate.localeCompare(a.event.finishDate) || b.event.id - a.event.id,
};

/**
 * Une los eventos de todas las fuentes por id, calcula sus roles de entrada y descarta los que
 * no tienen ninguno. Con `previous` fusiona una página nueva sobre lo ya cargado. No muta nada.
 */
export function mergeEventSources(
  entries: readonly EventSourceEntry[],
  previous: readonly EventAccessItem[] = [],
  order: EventListOrder = 'upcoming',
): EventAccessItem[] {
  const byId = [...previous, ...entries].reduce(
    (acc, entry) => new Map(acc).set(entry.event.id, mergeEntry(acc.get(entry.event.id), entry)),
    new Map<number, EventSourceEntry>(),
  );

  return [...byId.values()]
    .map(entry => ({ ...entry, roles: buildEventRoles(entry.flags) }))
    .filter(item => item.roles.length > 0)
    .sort(COMPARATORS[order]);
}
